import { useMutation, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '@/api/client'
import { trackDeviceEvent } from '@/lib/deviceDiagnostics'
import { ORDER_CONFIRMED_EVENT } from '../components/AutoPrinter'
import type {
  UpdateOrderStatusRequest,
  CancelOrderRequest,
  CancelOrderResponse,
  ConfirmOrderRequest,
  DeclineOrderRequest,
} from '../types'

interface UpdateStatusResponse {
  orderTime: number
  orderStatus: string
}

interface ConfirmOrderResponse {
  success: boolean
  confirmationStatus: string
}

/**
 * Kept exported so callers that catch this error keep compiling. The new admin
 * endpoints work for both super_admin and restaurant_admin sessions, so this
 * should never be thrown anymore.
 */
export class SuperAdminNotSupportedError extends Error {
  constructor() {
    super('This action is not supported for super admin accounts')
    this.name = 'SuperAdminNotSupportedError'
  }
}

export function useOrderActions(restaurantId: number, date: string) {
  const queryClient = useQueryClient()

  const updateStatus = useMutation({
    mutationFn: async ({ orderId, orderStatus }: UpdateOrderStatusRequest): Promise<UpdateStatusResponse> => {
      const response = await apiClient.post<UpdateStatusResponse>(
        `/api/admin/restaurants/${restaurantId}/orders/${orderId}/status`,
        { orderStatus }
      )
      return response.data
    },
    onSuccess: (_data, { orderId, orderStatus }) => {
      trackDeviceEvent('order.status_set', { orderId, status: orderStatus })
      void queryClient.invalidateQueries({ queryKey: ['orders', restaurantId, date] })
    },
  })

  const cancelOrder = useMutation({
    mutationFn: async ({ orderId, cancelReason }: CancelOrderRequest): Promise<CancelOrderResponse> => {
      const response = await apiClient.post<CancelOrderResponse>(
        `/api/admin/restaurants/${restaurantId}/orders/${orderId}/cancel`,
        { cancelReason }
      )
      return response.data
    },
    onSuccess: (_data, { orderId }) => {
      trackDeviceEvent('order.status_set', { orderId, status: 'cancelled' })
      void queryClient.invalidateQueries({ queryKey: ['orders', restaurantId, date] })
    },
  })

  const confirmOrder = useMutation({
    mutationFn: async ({ orderId, estimatedMinutes }: ConfirmOrderRequest): Promise<ConfirmOrderResponse> => {
      const response = await apiClient.post<ConfirmOrderResponse>(
        `/confirmOrder?restaurantId=${restaurantId}`,
        {
          orderId,
          estimatedMinutes,
        }
      )
      return response.data
    },
    onSuccess: (_data, { orderId }) => {
      trackDeviceEvent('order.status_set', { orderId, status: 'confirmed' })
      window.dispatchEvent(new CustomEvent(ORDER_CONFIRMED_EVENT, { detail: orderId }))
      void queryClient.invalidateQueries({ queryKey: ['orders', restaurantId, date] })
    },
  })

  const declineOrder = useMutation({
    mutationFn: async ({ orderId, reason, customReason }: DeclineOrderRequest): Promise<void> => {
      await apiClient.post(`/declineOrder?restaurantId=${restaurantId}`, {
        orderId,
        reason,
        customReason,
      })
    },
    onSuccess: (_data, { orderId }) => {
      trackDeviceEvent('order.status_set', { orderId, status: 'declined' })
      void queryClient.invalidateQueries({ queryKey: ['orders', restaurantId, date] })
    },
  })

  return {
    updateStatus,
    cancelOrder,
    confirmOrder,
    declineOrder,
    isUpdating: updateStatus.isPending,
    isCancelling: cancelOrder.isPending,
    isConfirming: confirmOrder.isPending,
    isDeclining: declineOrder.isPending,
  }
}
